import type { Session } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";

/**
 * A password-recovery session in progress. Set only when supabase-js
 * emits PASSWORD_RECOVERY, which it does after it has exchanged the
 * tokens from a genuine reset link for a session. A URL that merely says
 * "type=recovery" never sets this. Stored in localStorage next to the
 * session so a reload, or another tab of the same browser, sees it too;
 * cleared when the password is updated, the user signs out, or the
 * session present belongs to a different account. It is only exposed as
 * active once the session has loaded and matches it.
 */
export interface RecoveryState {
  userId: string;
  email: string | null;
}

const RECOVERY_KEY = "soundlayer:auth:recovery";

function readRecovery(): RecoveryState | null {
  try {
    const raw = localStorage.getItem(RECOVERY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RecoveryState;
    return parsed && typeof parsed.userId === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function writeRecovery(value: RecoveryState | null) {
  try {
    if (value) localStorage.setItem(RECOVERY_KEY, JSON.stringify(value));
    else localStorage.removeItem(RECOVERY_KEY);
  } catch {
    /* no storage: recovery lives only in memory for this page */
  }
}

interface AuthState {
  /** undefined while the persisted session is being read. */
  session: Session | null | undefined;
  /** Non-null while a reset link's session is waiting for a new password. */
  recovery: RecoveryState | null;
  clearRecovery: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  session: undefined,
  recovery: null,
  clearRecovery: () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(supabase ? undefined : null);
  const [stored, setStored] = useState<RecoveryState | null>(() => readRecovery());

  // Another tab of this browser may start or finish a recovery.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === RECOVERY_KEY) setStored(readRecovery());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setSession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      if (event === "PASSWORD_RECOVERY" && next?.user) {
        const value = { userId: next.user.id, email: next.user.email ?? null };
        writeRecovery(value);
        setStored(value);
      } else if (event === "SIGNED_OUT") {
        writeRecovery(null);
        setStored(null);
      }
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // A remembered recovery belongs to one account. If the session that is
  // actually present is a different user (or none), the recovery is stale.
  useEffect(() => {
    if (session === undefined || !stored) return;
    if (!session || session.user.id !== stored.userId) {
      writeRecovery(null);
      setStored(null);
    }
  }, [session, stored]);

  // Active only once the session has loaded and is the recovered account,
  // so a stale marker never renders a password form or redirects anyone.
  const recovery = session && stored && session.user.id === stored.userId ? stored : null;

  const value = useMemo<AuthState>(
    () => ({
      session,
      recovery,
      clearRecovery: () => {
        writeRecovery(null);
        setStored(null);
      },
      signOut: async () => {
        writeRecovery(null);
        setStored(null);
        await supabase?.auth.signOut();
      },
    }),
    [session, recovery],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
